import React from "react";
import { Link } from "react-router-dom";
import {
    SlidersHorizontal, Package, RotateCcw, Loader2, Flame
} from "lucide-react";
import ProductCard from "../ProductCard";
import PromoCard from "../PromoCard";
import { Button } from "../ui/button";
import { Skeleton } from "./CategorySubcategories";
import SunSpinner from "../SunSpinner";

export function CategoryBestsellers({ bestsellers, currentCat, hasFilters, searchQ, slug }) {
    if (!bestsellers.length || hasFilters || searchQ) return null;
    return (
        <div className="mb-6" data-testid="category-bestsellers">
            <div className="flex items-center gap-2 mb-3">
                <Flame size={16} className="text-orange-500" />
                <h3 className="text-sm font-bold text-gray-700">
                    Nejprodávanější{currentCat ? ` v ${currentCat.name}` : ""}
                </h3>
            </div>
            <div className="flex gap-3 overflow-hidden">
                {bestsellers.slice(0, 3).map(p => (
                    <Link key={`bs-${p.id}`} to={`/produkt/${p.slug}`}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-100 hover:border-green-200 hover:bg-green-50/50 transition-all group flex-1 min-w-0"
                        data-testid={`bestseller-${p.slug}`}>
                        <div className="w-12 h-12 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                            {p.main_image ? (
                                <img src={p.main_image} alt={p.name} className="w-full h-full object-contain p-0.5" loading="lazy" />
                            ) : (
                                <Package size={14} className="text-gray-300" />
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-700 group-hover:text-green-700 leading-tight line-clamp-1 transition-colors">{p.name}</p>
                            <p className="font-bold text-sm mt-0.5" style={{ color: "var(--sg-primary)" }}>
                                {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kč
                            </p>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}

export function ProductGrid({ products, viewMode, promoCards, promoFrequency, pageLimit, loading, total, hasMore, loadingMore, hasFilters, onLoadMore, onClearFilters, transitioning }) {
    return (
        <div className={`transition-opacity duration-200 ${transitioning ? "opacity-0" : "opacity-100"}`}>
            {loading && products.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16" data-testid="products-loading">
                    <SunSpinner size="lg" text="Nacitam produkty..." />
                </div>
            ) : products.length > 0 ? (
                <>
                    {viewMode === "b2b" && <B2BTable products={products} />}
                    {viewMode !== "b2b" && (
                        <div className={viewMode === "grid" ? "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" : "space-y-3"}
                            data-testid="products-container">
                            {products.map((p, i) => {
                                const items = [];
                                if (promoCards.length > 0 && promoFrequency > 0 && (i + 1) % promoFrequency === 0) {
                                    const cardIdx = Math.floor((i + 1) / promoFrequency) - 1;
                                    const card = promoCards[cardIdx % promoCards.length];
                                    if (card) {
                                        items.push(
                                            <div key={`promo-${i}`} className="animate-fade-in-up"
                                                style={{ animationDelay: `${Math.min(i % pageLimit, 11) * 25}ms`, animationFillMode: "both" }}>
                                                <PromoCard card={card} viewMode={viewMode} />
                                            </div>
                                        );
                                    }
                                }
                                items.push(
                                    <div key={p.id} className="animate-fade-in-up"
                                        style={{ animationDelay: `${Math.min(i % pageLimit, 11) * 25}ms`, animationFillMode: "both" }}>
                                        <ProductCard product={p} viewMode={viewMode} />
                                    </div>
                                );
                                return items;
                            })}
                        </div>
                    )}

                    {hasMore && (
                        <div className="flex flex-col items-center mt-10 gap-2">
                            <Button onClick={onLoadMore} disabled={loadingMore}
                                variant="outline" className="px-10 h-12 font-semibold border-2 rounded-full hover:border-green-400 hover:text-green-700 hover:bg-green-50"
                                data-testid="load-more-btn">
                                {loadingMore
                                    ? <><Loader2 size={15} className="mr-2 animate-spin" /> Načítám...</>
                                    : "Zobrazit další produkty"}
                            </Button>
                            <p className="text-xs text-gray-400">Zobrazeno {products.length} z {total}</p>
                        </div>
                    )}
                    {!hasMore && products.length >= pageLimit && (
                        <p className="text-center text-xs text-gray-400 mt-8 pt-4 border-t border-gray-100">
                            Všechny produkty zobrazeny ({total})
                        </p>
                    )}
                </>
            ) : !loading ? (
                <div className="bg-white border border-gray-100 rounded-2xl p-16 text-center">
                    <SlidersHorizontal size={40} className="mx-auto text-gray-200 mb-4" />
                    <h3 className="font-bold text-gray-700 mb-1 text-lg">Žádné produkty</h3>
                    <p className="text-sm text-gray-400 mb-5">Upravte nebo vymažte filtry</p>
                    {hasFilters && (
                        <Button onClick={onClearFilters} variant="outline" size="sm" className="border-green-300 text-green-700 hover:bg-green-50 rounded-full">
                            <RotateCcw size={12} className="mr-1.5" /> Vymazat filtry
                        </Button>
                    )}
                </div>
            ) : null}
        </div>
    );
}

function B2BTable({ products }) {
    return (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-4" data-testid="b2b-table-container">
            <table className="w-full text-xs">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase text-[10px]">SKU</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase text-[10px]">Produkt</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500 uppercase text-[10px]">Znacka</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-500 uppercase text-[10px]">Cena</th>
                        <th className="text-center px-3 py-2 font-medium text-gray-500 uppercase text-[10px]">Sklad</th>
                        <th className="px-3 py-2"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {products.map(p => (
                        <tr key={p.id} className="hover:bg-green-50/40 transition-colors" data-testid={`b2b-row-${p.id}`}>
                            <td className="px-3 py-2 font-mono text-gray-500 text-[11px]">{p.sku || "—"}</td>
                            <td className="px-3 py-2">
                                <Link to={`/produkt/${p.slug}`} className="text-gray-800 hover:text-green-700 font-medium text-sm truncate block max-w-xs">
                                    {p.name}
                                </Link>
                            </td>
                            <td className="px-3 py-2 text-gray-500">{p.brand || "—"}</td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-900">
                                {p.sale_price ? (
                                    <><span className="text-red-600">{p.sale_price.toLocaleString("cs-CZ")} Kc</span> <span className="text-gray-400 line-through text-[10px]">{p.price?.toLocaleString("cs-CZ")}</span></>
                                ) : (
                                    <>{(p.price || 0).toLocaleString("cs-CZ")} Kc</>
                                )}
                            </td>
                            <td className="px-3 py-2 text-center">
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${p.stock_status === "in_stock" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                    {p.stock_status === "in_stock" ? "Skladem" : "Vyprodano"}
                                </span>
                            </td>
                            <td className="px-3 py-2">
                                <Link to={`/produkt/${p.slug}`} className="text-green-600 hover:text-green-800 text-[10px] font-semibold">Detail</Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
